import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { BadgeTheme } from '../../Badge'
import { Hr } from '../../Hr'
import { Link } from '../../Link'
import { BaseLink } from '../../Links'
import { Picture } from '../../Picture'
import { ProductScoreGauge } from '../../ProductScoreGauge'
import { TypographyV2 } from '../../Seatbelt2/Foundations/Typography'
import { ProductCardBadges } from '../ProductCards/shared/ProductCardBadges'
import styles from './RelatedProductsCard.module.scss'

export const RelatedProductsCard: FunctionComponent<Props> = ({
  href,
  price,
  model,
  badges,
  overallScore,
  imageSrc,
  hasRetailers,
  manufacturer,
  className,
  textVariation,
  label,
  toolTip,
  ...rest
}) => {
  const commonLinkProps = {
    href,
    'data-testid': 'single-product-review-link',
    'data-which-id': 'related-products-card',
  }

  return (
    <article
      className={classnames(styles.relatedProductCard, className)}
      data-testid="product-card-wrapper"
      {...rest}
    >
      <BaseLink {...commonLinkProps}>
        <ProductCardBadges badges={badges} data-testid="badges" />
        <span className={styles.imageSectionLink}>
          {imageSrc && (
            <span className={styles.pictureContainer}>
              <Picture
                imageClassName={styles.picture}
                alt={`${manufacturer} ${model} front view`}
                src={imageSrc}
              />
            </span>
          )}
        </span>

        <TypographyV2 tag="h2" textStyle="sb-text-interface-heading-large">
          {manufacturer}
        </TypographyV2>

        <TypographyV2
          tag="h3"
          textStyle="sb-text-body-default-regular"
          className="sb-margin-bottom-3"
        >
          {model}
        </TypographyV2>

        <TypographyV2
          tag="span"
          textStyle="sb-text-interface-heading-large"
          className={styles.price}
        >
          {price.formatted}
        </TypographyV2>
      </BaseLink>

      {hasRetailers ? (
        <Link
          href={`${href}#compare-prices`}
          textStyle="sb-text-interface-body-small-regular"
          data-testid="single-product-retailer-link"
          data-which-id="related-products-retailer-link"
          aria-label={`View retailers where you can buy ${manufacturer} ${model}`}
        >
          View retailers
        </Link>
      ) : (
        <Link
          {...commonLinkProps}
          textStyle="sb-text-interface-body-small-regular"
          className={styles.label}
          includeAnimation={false}
        >
          {price.label}
        </Link>
      )}

      <BaseLink {...commonLinkProps}>
        <Hr
          className="sb-margin-vertical-3 sb-bg-color-black-20"
          fullWidth={true}
        />
        <ProductScoreGauge
          testScoreGauge={{ overallScore, textVariation }}
          label={label}
          tooltip={toolTip}
          className={styles.scoreGauge}
        />
      </BaseLink>
    </article>
  )
}

///////// IMPLEMENTATION /////////

export type Props = {
  /**
   * Link to product review page
   */
  href: string
  /**
   * Name of the manufacturer
   */
  manufacturer: string
  /**
   * Price of the item along with a label
   */
  price: {
    formatted: string
    label: string
  }
  /**
   * Model number
   */
  model: string
  /**
   * Array of badges awarded to product, empty if user is not logged in
   */
  badges?: Array<BadgeTheme> | null
  /**
   * Review score, nulled out if user is not logged in
   */
  overallScore?: number
  /**
   * Array of objects for the front, side and back images of the product
   */
  imageSrc: string
  /**
   * Array of objects if we have any retailers listed on the product review page
   */
  hasRetailers: boolean
  /**
   * Optional classname
   */
  className?: string
  label?: string
  textVariation?: 'obfuscated'
  toolTip: {
    ariaLabel: string
    contents: string
  }
} & ComponentPropsWithoutRef<'article'>
